import {useState} from "react";
import {useWindowedList, type WindowedRowProps} from "components/windowedList/useWindowedList";
import {DragOnly} from "components/Accessibility.styles";
import {useLocale, useMessages} from "i18n";
import formatBytes from "../../core/format/byteFormat";
import {folderPrefixOf} from "core/transfer/relativePath";
import {brand} from "../../theme/tokens";
import {senderFileKind} from "../../download/fileKind";
import {Mode, Plan} from "../gating";
import {UsageMeter} from "./UsageMeter";
import {useBrowse} from "./PageDropLayer";
import {
	AddHint, AddMore, AddRow, CollapseArrow, CollapsedIcon, CollapsedMeta, CollapsedRow, CollapsedSub, CollapsedTitle,
	FILE_ROW_HEIGHT_ESTIMATE_PX, FileInfo, FileName, FileRow, FileSize, ListScroll, RemoveButton, Summary, Thumb,
	UsageHeader,
} from "./FileSummary.styles";

/**
 * What the list renders per selected file — a raw `File` satisfies it (flat), the create flow
 * passes path-aware view models since ui#94 so folder pickups show where each file came from.
 */
export interface SummaryFile {
	name: string;
	size: number;
	/** Folder-relative path INCLUDING the leaf name (`docs/report.pdf`); absent for plain files. */
	relativePath?: string;
}

interface Props {
	files: SummaryFile[];
	mode: Mode;
	plan: Plan;
	onRemove: (index: number) => void;
	/**
	 * Whether files can be added from this device. False for files the account already stores
	 * (kitecorp/transferify-ui#3405): a new link to them cannot take an upload beside them.
	 */
	canAdd?: boolean;
}

const COLLAPSE_THRESHOLD = 3; // collapse the list when there are MORE than 3 files

/** White "stacked copies" glyph inside the collapsed row's gradient icon (design `.coll-ico`). */
function StackGlyph() {
	return (
		<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={brand.white} strokeWidth="2"
			 strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
			<rect x="3" y="8" width="13" height="13" rx="2"/>
			<path d="M8 8V5a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-3"/>
		</svg>
	);
}

/**
 * The payload box on the create card: the usage/payload header, the file list, and the collapse /
 * "+ add more" affordances. ≤3 files list inline; more collapse into one expandable "N files · total"
 * row. Both add affordances are wired to the page-level browse (no-op outside {@link PageDropLayer}).
 *
 * Reflowed in ui#216 — the {@link UsageMeter} payload header moved from the BOTTOM of this box to the
 * TOP, where it pins: it is the first thing the sender reads, and the file rows scroll underneath it
 * rather than pushing it off-screen. Top → bottom: **usage header (pinned) → file list (the card's one
 * scroller) → show-less / + add more (pinned)**. The flex/overflow contract that makes that list the
 * card's ONLY scroll region is documented in {@link ./FileSummary.styles}.
 */
export function FileSummary({files, mode, plan, onRemove, canAdd = true}: Props) {
	const [expanded, setExpanded] = useState(false);
	const {browseFiles, browseFolder} = useBrowse();
	const {sender} = useMessages();
	const {locale} = useLocale();
	const totalBytes = files.reduce((sum, f) => sum + f.size, 0);
	const collapsible = files.length > COLLAPSE_THRESHOLD;
	const shouldShowList = !collapsible || expanded;
	// A 10,000-file selection draws only the rows near the view of ListScroll (kitecorp/transferify-ui#2942).
	const list = useWindowedList<HTMLDivElement>({count: files.length, estimateRowHeight: FILE_ROW_HEIGHT_ESTIMATE_PX});

	const fileRow = (file: SummaryFile, index: number, props: WindowedRowProps) => {
		const kind = senderFileKind(file.name);
		// ui#94: sanitized folder prefix as secondary text, so the sender SEES the structure sent.
		const folder = folderPrefixOf(file.name, file.relativePath);
		return (
			<FileRow key={`${file.relativePath ?? file.name}-${index}`} {...props}>
				<Thumb data-testid="file-kind-badge" $tone={kind.tone}>{kind.label}</Thumb>
				<FileInfo>
					<FileName title={folder !== null ? `${folder}/${file.name}` : file.name}>{file.name}</FileName>
					<FileSize>{formatBytes(file.size, locale, 1)}{folder !== null ? ` · ${folder}/` : ""}</FileSize>
				</FileInfo>
				<RemoveButton type="button" aria-label={sender.removeFileAria(file.name)} onClick={() => onRemove(index)}>
					✕
				</RemoveButton>
			</FileRow>
		);
	};

	// The download-limit card's top edge lines up with this box (ui#2468).
	return (
		<Summary data-slide-out-anchor="">
			{/* Pinned payload header (ui#216) — stays put while the rows below it scroll. */}
			<UsageHeader>
				<UsageMeter mode={mode} plan={plan} totalBytes={totalBytes} fileCount={files.length}/>
			</UsageHeader>
			{collapsible && !expanded && (
				<CollapsedRow type="button" aria-expanded={false} onClick={() => setExpanded(true)}>
					<CollapsedIcon><StackGlyph/></CollapsedIcon>
					<CollapsedMeta>
						<CollapsedTitle>{sender.filesCount(files.length)}</CollapsedTitle>
						<CollapsedSub>{formatBytes(totalBytes, locale, 1)}</CollapsedSub>
					</CollapsedMeta>
					<CollapseArrow>▾</CollapseArrow>
				</CollapsedRow>
			)}
			{shouldShowList && (
				<>
					<ListScroll ref={list.listRef} onFocus={list.onFocus} onBlur={list.onBlur}>
						{list.rows.map(({index, props}) => fileRow(files[index], index, props))}
					</ListScroll>
					{collapsible && (
						<CollapsedRow type="button" aria-expanded onClick={() => setExpanded(false)}>
							<CollapseArrow>▴</CollapseArrow>
							<CollapsedSub>{sender.showLess}</CollapsedSub>
						</CollapsedRow>
					)}
				</>
			)}
			{canAdd && <>
				{/* The same pair the empty hero offers, kept available once a selection exists (ui#1314). */}
				<AddRow>
					<AddMore type="button" onClick={browseFiles}>＋ {sender.addMore}</AddMore>
					<AddMore type="button" onClick={browseFolder}>＋ {sender.addMoreFolder}</AddMore>
				</AddRow>
				{/*
				  * The drag hint sits UNDER the pair since ui#1314, not inside the files button. It always
				  * described the whole drop area rather than that one control, and inside a half-width
				  * button it pushed the row to three lines in Romanian — enough extra card height for the
				  * landing to stop finding room for its campaign below the card (see AddRow).
				  * It names a DRAG, so it is still shown only where one is possible (ui#1306), and
				  * `DragOnly` stays the OUTER element so no empty line is left where it is hidden.
				  */}
				<DragOnly><AddHint>{sender.addMoreHint}</AddHint></DragOnly>
			</>}
		</Summary>
	);
}
